import { FormEvent, useState } from 'react'
import { IconSearch } from '@tabler/icons-react'
import { useRouter } from 'next/router'
import { GetProductListResponse, ProductSummary } from 'shared-types'
import { Button } from '~/components/Button'
import { Dialogue } from '~/components/Dialogue'
import Input from '~/components/Input'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import { usePage, useSession } from '~/hooks'
import { Client } from '~/customClients/client'
import styles from './ProductSearchModal.module.css'
import { ProductSearchModalProps } from './ProductSearchModal.types'
import { ProductSelectionCard } from './ProductSelectionCard'

const PRODUCT_SHOW_COUNT = 3

export const ProductSearchModal = ({
  open,
  onClose,
  onSelect,
}: ProductSearchModalProps) => {
  const router = useRouter()
  const { session } = useSession()
  const [keyword, setKeyword] = useState('')
  const [totalCount, setTotalCount] = useState(0)
  const [showCount, setShowCouont] = useState(PRODUCT_SHOW_COUNT)
  const [products, setProducts] = useState<ProductSummary[]>([])

  const handleSearchSubmit = async (e?: FormEvent<HTMLFormElement>) => {
    e?.preventDefault()
    e?.stopPropagation()

    if (!session || !keyword) {
      setProducts([])
      return
    }

    const res = await Client.productList.getProductList(`q=${keyword}`, session)

    const { products = [], totalProducts = 0 }: GetProductListResponse = res

    setProducts(products)
    setTotalCount(totalProducts)
    setShowCouont(PRODUCT_SHOW_COUNT)
  }

  const handleMoreProducts = () => {
    if (showCount < totalCount) {
      setShowCouont((count) => {
        return Math.min(count + PRODUCT_SHOW_COUNT, totalCount)
      })
      return null
    }
    return router.push(`/search?q=${encodeURIComponent(keyword)}`)
  }

  const handleBack = () => {
    return onClose && onClose()
  }

  const page = usePage()
  const isRTM = page.brand === 'rtm'
  return (
    <Dialogue
      size='md'
      cancelText=''
      okText='Back'
      visible={open}
      onCancel={onClose}
      onConfirm={handleBack}
      wrapperClassName={styles.root}
      modalClass={styles.modal}>
      <div>
        <Typography tag={TypographyTag.p} variant={TypographyVariant.Heading6}>
          Add product
        </Typography>
        <Typography
          tag={TypographyTag.p}
          variant={
            isRTM
              ? TypographyVariant.BodyRegularExtraLineHeight
              : TypographyVariant.BodyRegularExtraLineHeightBold
          }
          className='mt-2 text-secondary-muted'>
          Search for a Jaycar product and we can automatically fill in the
          details.
        </Typography>
        <form onSubmit={handleSearchSubmit} className='pt-6 pb-8'>
          <Input
            name='search'
            label='Search for a Jaycar Product by name'
            placeholder='Product name'
            suffix={
              <Button
                size='sm'
                shape='circle'
                variant='ghost'
                theme={{ root: styles.inputBtn }}
                icon={<IconSearch color='var(--color-typography-heading)' />}
                iconOnly
                onClick={() => {
                  return handleSearchSubmit()
                }}
              />
            }
            value={keyword || ''}
            onChange={(v) => {
              return setKeyword(v)
            }}
          />
        </form>
      </div>
      {!!products.length && (
        <div className='w-full flex flex-col gap-6 text-grayscale-900'>
          <Typography
            tag={TypographyTag.div}
            variant={TypographyVariant.BodyRegularBold}>
            Showing {Math.min(products.length, showCount)} of {totalCount}{' '}
            results
          </Typography>
          <div className='max-h-80 overflow-y-auto overflow-x-hidden flex flex-col gap-6 divide-y'>
            {products.slice(0, showCount).map((product, index) => {
              return (
                <div key={index} className='pt-6'>
                  <ProductSelectionCard
                    product={product}
                    isSelected={false}
                    onSelect={() => {
                      return onSelect?.(product)
                    }}
                  />
                </div>
              )
            })}
          </div>
          <Button
            className='w-min'
            style={{ '--btn-size-md-height': '32px' }}
            status='secondary'
            onClick={handleMoreProducts}>
            Show more
          </Button>
        </div>
      )}
      <div className='pt-8 flex justify-end'>
        <Button status='secondary' onClick={onClose}>
          Back
        </Button>
      </div>
    </Dialogue>
  )
}
